<template>
  <MaskedInput
    v-model="innerValue"
    preset="cccd"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :status="computedStatus"
    :unmasked="unmasked"
    @blur="onBlur"
    @pressEnter="onPressEnter"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import MaskedInput from "./MaskedInput.vue";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  unmasked?: boolean;
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "Nhập CCCD (12 số)",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  unmasked: true,
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "validate", valid: boolean): void;
}>();

const isValidCccd = (value: string) => {
  if (!value) return true;
  return /^\d{12}$/.test(value);
};

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  return !!props.modelValue && !isValidCccd(props.modelValue);
});

const computedStatus = computed(() => props.status || (localError.value ? "error" : ""));

const innerValue = computed({
  get: () => props.modelValue ?? "",
  set: (val: string) => {
    emit("update:modelValue", val);
    emit("change", val);
  },
});

const onChange = (val: string) => {
  emit("update:modelValue", val);
  emit("change", val);
};

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidCccd(props.modelValue ?? ""));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — CCCD
  Global: <h-cccd v-model="cccd" />
  Import: import { CccdInput } from '@/components/ant/input'

  Props: modelValue, unmasked, validateOnBlur...
  Events: update:modelValue, change, blur, pressEnter, validate
  Docs: src/components/ant/input/README.md
-->
